import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ChevronDown } from '../../../assets/icons'
import { InputContainer, InputWrapper, Label, StyledInput, HelperText } from '../input/styled'
import { DropdownList, DropdownOption, ErrorMessage, SelectIcon } from '../select/styled'

const Autocomplete = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = '',
  error,
  disabled = false,
  required = false,
  helperText,
  assignFieldValue,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const wrapperRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const selectedOption = options.find(opt => opt.value === value)
    setInputValue(selectedOption ? selectedOption.label : value || '')
  }, [value, options])

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
        setFocusedIndex(-1)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const filteredOptions = options.filter(option => {
    const label = option && option.label ? option.label.toString() : ''
    const search = inputValue ? inputValue.toString() : ''
    return label.toLowerCase().includes(search.toLowerCase())
  })

  useEffect(() => {
    setIsOpen(
      filteredOptions.length > 0 && document.activeElement === inputRef.current && !!inputValue
    )
  }, [inputValue, options])

  const handleInputChange = e => {
    const val = e.target.value
    setInputValue(val)
    setIsOpen(true)
    setFocusedIndex(-1)
    if (onChange) onChange({ target: { name, value: val } })
  }

  const handleOptionClick = option => {
    setInputValue(option.label)
    setIsOpen(false)
    setFocusedIndex(-1)
    if (onChange) onChange({ target: { name, value: option.value } })

    if (assignFieldValue) {
      assignFieldValue(option.value)
    }
  }

  const handleFocus = () => {
    if (filteredOptions.length > 0) setIsOpen(true)
  }

  const handleBlur = () => {
    setTimeout(() => setIsOpen(false), 120)
  }

  const handleKeyDown = e => {
    if (!isOpen) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
        break
      case 'Enter':
        if (focusedIndex >= 0) {
          e.preventDefault()
          handleOptionClick(filteredOptions[focusedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setFocusedIndex(-1)
        break
      default:
        break
    }
  }

  return (
    <InputWrapper ref={wrapperRef}>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="req">*</span>}
        </Label>
      )}
      <InputContainer style={{ position: 'relative' }}>
        <StyledInput
          ref={inputRef}
          id={name}
          name={name}
          value={inputValue}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          $error={!!error}
          {...props}
        />
        <SelectIcon $isOpen={isOpen}>
          <ChevronDown />
        </SelectIcon>
        {isOpen && (
          <DropdownList style={{ width: '100%' }}>
            {filteredOptions.length === 0 ? (
              <DropdownOption disabled>Sin resultados</DropdownOption>
            ) : (
              filteredOptions.map((option, idx) => (
                <DropdownOption
                  key={option.value}
                  onClick={() => handleOptionClick(option)}
                  $isSelected={idx === focusedIndex}
                  style={idx === focusedIndex ? { background: '#e0e0e0', fontWeight: 600 } : {}}
                  tabIndex={-1}
                >
                  {option.label}
                </DropdownOption>
              ))
            )}
          </DropdownList>
        )}
      </InputContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <HelperText>{helperText}</HelperText>
    </InputWrapper>
  )
}

export default Autocomplete
